import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import SEO from '@/components/SEO';
import axios from 'axios';
import { ChevronRight, Filter, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ObiecteSanitarePage = () => {
  const { subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(subcategory || 'all');
  const [showFilters, setShowFilters] = useState(false);
  const [cms, setCms] = useState({});

  const subcategories = [
    { slug: 'all', name: 'Toate' },
    { slug: 'cazi', name: 'Căzi' },
    { slug: 'pereti-dus', name: 'Pereți de Duș' },
    { slug: 'cabine-dus', name: 'Cabine de Duș' },
    { slug: 'wc', name: 'WC' },
    { slug: 'lavoar', name: 'Lavoar' },
    { slug: 'bideu', name: 'Bideu' },
    { slug: 'baterii', name: 'Baterii' },
  ];

  useEffect(() => {
    fetchProducts();
  }, [subcategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [prodRes, cmsRes] = await Promise.all([
        axios.get(subcategory ? `${API_URL}/api/products?subcategory=${subcategory}` : `${API_URL}/api/products?category=obiecte-sanitare`),
        axios.get(`${API_URL}/api/pages/sanitare`)
      ]);
      setProducts(prodRes.data);
      if (cmsRes.data.content) setCms(cmsRes.data.content);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (slug) => {
    setActiveFilter(slug);
    setShowFilters(false);
  };

  const filteredProducts = activeFilter === 'all' 
    ? products 
    : products.filter(p => p.subcategory_slug === activeFilter);

  return (
    <Layout>
      <SEO
        title={cms.hero_title || 'Obiecte Sanitare - Căzi, Cabine Duș, WC'}
        description={cms.hero_description || 'Obiecte sanitare premium: căzi, cabine de duș, WC, lavoare, baterii. Shagun Surfaces Brașov.'}
        canonicalUrl={`https://shagunsurfaces.ro/obiecte-sanitare${subcategory ? '/' + subcategory : ''}`}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#050505]" data-testid="obiecte-sanitare-hero">
        {cms.hero_image && (
          <div className="absolute inset-0">
            <img src={cms.hero_image.startsWith('/api') ? `${API_URL}${cms.hero_image}` : cms.hero_image} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 to-[#050505]" />
          </div>
        )}
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#A3A3A3] hover:text-[#D4AF37]">Acasă</Link>
            <ChevronRight size={14} className="text-[#A3A3A3]" />
            <span className="text-[#D4AF37]">Obiecte Sanitare</span>
            {subcategory && (
              <>
                <ChevronRight size={14} className="text-[#A3A3A3]" />
                <span className="text-white capitalize">{subcategory.replace('-', ' ')}</span>
              </>
            )}
          </nav>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            {cms.hero_title || 'Obiecte Sanitare'}
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-2xl">
            {cms.hero_description || 'Completează-ți baia cu obiecte sanitare de calitate superioară: căzi, cabine de duș, WC-uri, lavoare și baterii elegante.'}
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12 bg-[#0a0a0a]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 text-[#A3A3A3] border border-white/10 px-4 py-2"
              >
                <Filter size={18} />
                Filtrează
              </button>
              
              {/* Desktop Filters */}
              <div className="hidden lg:flex flex-wrap items-center gap-2">
                {subcategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/obiecte-sanitare' : `/obiecte-sanitare/${cat.slug}`}
                    onClick={() => handleFilter(cat.slug)}
                    className={`px-4 py-2 text-sm font-medium transition-all ${
                      activeFilter === cat.slug
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-[#121212] text-[#A3A3A3] hover:text-white border border-white/10'
                    }`}
                    data-testid={`filter-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <p className="text-[#A3A3A3] text-sm">
              {filteredProducts.length} produse
            </p>
          </div>

          {/* Mobile Filters Modal */}
          {showFilters && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/80" onClick={() => setShowFilters(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-[#121212] p-6 rounded-t-2xl max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-serif text-xl">Filtrează</h3>
                  <button onClick={() => setShowFilters(false)} className="text-[#A3A3A3]">
                    <X size={24} />
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {subcategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'all' ? '/obiecte-sanitare' : `/obiecte-sanitare/${cat.slug}`}
                      onClick={() => handleFilter(cat.slug)}
                      className={`px-6 py-4 text-center font-medium transition-all ${
                        activeFilter === cat.slug
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-[#050505] text-[#A3A3A3] border border-white/10'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-[#121212] aspect-square animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#A3A3A3] text-lg mb-4">
                Momentan nu avem produse în această categorie.
              </p>
              <p className="text-[#A3A3A3] text-sm">
                Revenți în curând sau contactați-ne pentru mai multe detalii.
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ObiecteSanitarePage;
